import { QueryClient } from '@tanstack/react-query';
import { equals } from 'ramda';
import { useCallback, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Module } from 'src/domain';
import { useChangesSnackbar } from 'src/hooks/snackbar/useChangesSnackbar';
import {
    CreateSalesCampaignDiscountInput,
    UpdateSalesCampaignDiscountInput,
} from 'src/infrastructure/rest-api/api-types';
import { useApiMutation } from 'src/infrastructure/rest-api/useApi';
import { mapSalesCampaignDiscountToInput } from 'src/utils/salesCampaignDiscount';
import { useDiscardChangesModule } from '../discard-changes/useDiscardChangesModule';
import { useCompositePartner } from '../partner/useCompositePartner';
import { useSalesCampaignRows } from './useSalesCampaignRows';
import { useSalesCampaignSimulationRows } from './useSalesCampaignSimulationRows';

export const useSalesCampaignPlanningSaveChanges = () => {
    const { id: salesCampaignId } = useParams();
    const [isSaving, setIsSaving] = useState(false);
    const { data } = useSalesCampaignRows(Number(salesCampaignId));
    const { allData: simulationData } = useSalesCampaignSimulationRows(Number(salesCampaignId));
    const discardChanges = useDiscardChangesModule(Module.SalesCampaignsPlanning);
    const compositePartner = useCompositePartner();
    const showSnackbar = useChangesSnackbar();

    const invalidateStyleTimelineQueries = (queryClient: QueryClient) => {
        // Invalidate all partner styletimeline queries so active sales campaign ids/names are refreshed.
        queryClient.invalidateQueries({
            predicate: (query) => {
                const key = query.queryKey[0];
                return typeof key === 'string' && key.startsWith('/api/styletimeline/partners/');
            },
        });
    };

    const [createDiscount] = useApiMutation('/api/salescampaigns/discounts', 'post', {
        update(data, _variables, queryClient) {
            queryClient.invalidateQueries({
                queryKey: [`/api/salescampaigns/partners/${compositePartner?.id}`, undefined],
            });
            queryClient.invalidateQueries({
                queryKey: [`/api/salescampaigns/phase/${data[0].salesCampaignPhaseId}/discounts`, undefined],
            });

            invalidateStyleTimelineQueries(queryClient);
        },
    });

    const [updateDiscount] = useApiMutation('/api/salescampaigns/discounts', 'put', {
        update(data, _variables, queryClient) {
            queryClient.invalidateQueries({
                queryKey: [`/api/salescampaigns/partners/${compositePartner?.id}`, undefined],
            });
            queryClient.invalidateQueries({
                queryKey: [`/api/salescampaigns/phase/${data[0].salesCampaignPhaseId}/discounts`, undefined],
            });

            invalidateStyleTimelineQueries(queryClient);
        },
    });

    const [deleteDiscount] = useApiMutation('/api/salescampaigns/discounts', 'delete', {
        update(_data, _variables, queryClient) {
            queryClient.invalidateQueries({
                queryKey: [`/api/salescampaigns/partners/${compositePartner?.id}`, undefined],
            });

            queryClient.invalidateQueries({
                queryKey: [
                    `/api/salescampaigns/phase/${simulationData[0].discount.salesCampaignPhaseId}/discounts`,
                    undefined,
                ],
            });

            invalidateStyleTimelineQueries(queryClient);
        },
    });

    const update = useCallback((): Promise<boolean> => {
        if (!simulationData) {
            // No simulation data exists
            return Promise.resolve(true);
        }

        const changedRows = simulationData?.filter((row) => {
            const originalRow = data?.find((r) => r.id === row.id);
            return !equals(originalRow?.discount, row.discount);
        });

        if (changedRows.length <= 0) {
            // No changes to save
            return Promise.resolve(true);
        }

        const discounts = changedRows.map((data) => data.discount);
        // Changed rows can contain 'virtual' rows that do not have an id
        // (i.e. rows that do not have a discount from the backend yet)
        // Some of them could be valid, cause user changed the discount type or value,
        // but some of them could be invalid, cause user removed the discount or it was never changed by the user.
        // Filter out rows that do not have all required values
        const toUpdateOrCreate = discounts.filter((d) => {
            return !!(d?.valueX && d?.valueY);
        });

        // Create list for updates and list for creates
        const toUpdate = toUpdateOrCreate
            ?.filter((d) => d.id)
            .map(mapSalesCampaignDiscountToInput) as UpdateSalesCampaignDiscountInput[];
        const toCreate = toUpdateOrCreate
            ?.filter((d) => !d.id)
            .map(mapSalesCampaignDiscountToInput) as CreateSalesCampaignDiscountInput[];
        const toDelete = discounts
            .filter((d) => {
                return !!(d?.id && (!d?.valueX || !d?.valueY));
            })
            .map(
                (d) =>
                    ({
                        salesCampaignDiscountId: d.id,
                    }) as { salesCampaignDiscountId: number }
            );

        setIsSaving(true);

        const promises: Promise<unknown>[] = [];

        if (toUpdate.length > 0) {
            promises.push(updateDiscount({ body: toUpdate }));
        }

        if (toCreate.length > 0) {
            promises.push(createDiscount({ body: toCreate }));
        }

        if (toDelete.length > 0) {
            promises.push(deleteDiscount({ body: toDelete }));
        }

        return Promise.all(promises)
            .then(async () => {
                await discardChanges();
                return true;
            })
            .catch((error: unknown) => {
                showSnackbar(error);
                return false;
            })
            .finally(() => setIsSaving(false));
    }, [data, simulationData, updateDiscount, createDiscount, deleteDiscount, discardChanges, showSnackbar]);

    return [update, { isSaving }] as const;
};
